import React, { useRef } from 'react';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import { Modal } from '../Modal/Modal';
import { Select } from '../Select/Select';
import { Input } from '../Input/Input';
import { Textarea } from '../Textarea/Textarea';
import { Button } from '../Button/Button';
import { Checkbox } from '../Checkbox/Checkbox';
import { Typography } from '../Typography/Typography';
import { KpiItem, KpiPeriod } from '../../types';
import { ReactComponent as InfoIcon } from '../../../_shared/assets/icons/info-icon.svg';

import styles from './KpiFormModal.module.scss';

const UNITS = ['%', 'шт.', 'руб.', 'балл', 'ед.'];
const DEPARTMENTS = ['Департамент A', 'Департамент B', 'Департамент C'];
const METHODOLOGIES = ['Метод 1', 'Метод 2', 'Метод 3'];
const PERIODS: KpiPeriod[] = [6, 9, 12];

interface FormValues {
  name: string;
  unit: string;
  planValue: string;
  weight: string;
  department: string;
  methodology: string;
  methodologyFile: File | null;
  periods: KpiPeriod[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: Omit<KpiItem, 'id' | 'years'>) => void;
  initialValues?: Partial<FormValues>;
  notification?: string;
}

function validate(values: FormValues) {
  const errors: Partial<Record<keyof FormValues, string>> = {};
  if (!values.name.trim()) errors.name = 'Обязательное поле';
  if (!values.unit) errors.unit = 'Обязательное поле';
  if (!values.planValue) errors.planValue = 'Обязательное поле';
  if (!values.weight) errors.weight = 'Обязательное поле';
  if (!values.department) errors.department = 'Обязательное поле';
  if (!values.methodology && !values.methodologyFile) {
    errors.methodology = 'Выберите методику или загрузите документ';
  }
  if (values.periods.length === 0)
    {errors.periods = 'Выберите хотя бы один период';}
  return errors;
}

const defaultValues: FormValues = {
  name: '',
  unit: '',
  planValue: '',
  weight: '',
  department: '',
  methodology: '',
  methodologyFile: null,
  periods: []
};

export function KpiFormModal({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  notification
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formik = useFormik<FormValues>({
    initialValues: { ...defaultValues, ...initialValues },
    validate,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSubmit({
        name: values.name.trim(),
        unit: values.unit,
        weight: Number(values.weight),
        department: values.department,
        methodologyUrl: values.methodology || undefined,
        methodologyFile: values.methodologyFile || undefined,
        periods: values.periods
      });
      onClose();
    }
  });

  function togglePeriod(period: KpiPeriod) {
    const current = formik.values.periods;
    const next = current.includes(period)
      ? current.filter((p) => p !== period)
      : [...current, period];
    formik.setFieldValue('periods', next);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    formik.setFieldValue('methodologyFile', file);
    if (file) formik.setFieldValue('methodology', '');
  }

  const isEdit = !!initialValues?.name;
  const title = isEdit ? 'Редактирование КПЭ' : 'Добавление КПЭ';
  const submitLabel = isEdit ? 'Сохранить' : 'Добавить КПЭ';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <button className={styles.closeIcon} onClick={onClose}>
        <CloseIcon />
      </button>
      <div className={styles.kpiModal}>
        <div className={styles.head}>
          <Typography variant="h2" className={styles.title}>
            {title}
          </Typography>

          {notification && (
            <div className={styles.notification}>
              <span className={styles.notificationIcon}>
                <InfoIcon />
              </span>
              <Typography variant="text">{notification}</Typography>
            </div>
          )}
        </div>

        <form onSubmit={formik.handleSubmit} noValidate>
          <div className={styles.fields}>
            <Textarea
              label="Название КПЭ"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              rows={3}
              error={formik.touched.name ? formik.errors.name : undefined}
            />

            <Select
              label="Единицы измерения"
              options={UNITS}
              value={formik.values.unit}
              onChange={(v) => formik.setFieldValue('unit', v ?? '')}
              error={formik.touched.unit ? formik.errors.unit : undefined}
            />
            <div className={styles.fieldRow}>
              <Input
                  label="Плановое значение"
                  name="planValue"
                  type="number"
                  value={formik.values.planValue}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.planValue ? formik.errors.planValue : undefined
                  }
              />

              <Input
                  label="Вес КПЭ"
                  name="weight"
                  type="number"
                  value={formik.values.weight}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.weight ? formik.errors.weight : undefined}
              />
            </div>


            <Select
              label="Оценивающее подразделение"
              options={DEPARTMENTS}
              value={formik.values.department}
              onChange={(v) => formik.setFieldValue('department', v ?? '')}
              error={
                formik.touched.department ? formik.errors.department : undefined
              }
            />

            <div className={styles.field}>
              <Typography variant="h5">Методика оценивания</Typography>
              <div className={styles.methodologyRow}>
                <Select
                  label="Выбрать из имеющихся"
                  options={METHODOLOGIES}
                  value={formik.values.methodology}
                  onChange={(v) => {
                    formik.setFieldValue('methodology', v ?? '');
                    if (v) formik.setFieldValue('methodologyFile', null);
                  }}
                  className={styles.methodologySelect}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  className={styles.fileInput}
                  onChange={handleFileChange}
                />
                {formik.values.methodologyFile && (
                  <div className={styles.fileChip}>
                    <span className={styles.fileName}>{formik.values.methodologyFile.name}</span>
                    <button
                        type="button"
                        className={styles.fileRemoveBtn}
                        onClick={() => formik.setFieldValue('methodologyFile', null)}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                )}
                <Button
                  variant="secondary"
                  className={styles.fileBtn}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Добавить документ <UploadIcon className={styles.uploadIcon} />
                </Button>
              </div>
              {formik.touched.methodology && formik.errors.methodology && (
                <span className={styles.error}>
                  {formik.errors.methodology}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <Typography variant="h5">Период оценивания</Typography>
              <div className={styles.periods}>
                {PERIODS.map((period) => (
                  <Checkbox
                    key={period}
                    checked={formik.values.periods.includes(period)}
                    onChange={() => togglePeriod(period)}
                    label={`${period} месяцев`}
                  />
                ))}
              </div>
              {formik.touched.periods && formik.errors.periods && (
                <span className={styles.error}>
                  {formik.errors.periods as string}
                </span>
              )}
            </div>
          </div>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={onClose} type="button">
              Отмена
            </Button>
            <Button variant="primary" type="submit">
              {submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
